






























































































































































import Vue from 'vue';
import {
  brand,
  Brand,
  booth,
  Booth,
  item,
  Item,
  analytics,
  buyer as buyerM,
  Buyer,
  buyerCompany as buyerCompanyM,
  BuyerCompany,
  favorite,
  contact,
  createNewContact,
  calender,
  FirebaseID,
} from 'mostra';

export type VisitorInfo = {
  uid: FirebaseID;
  companyName: string;
  visitedTarget: string;
  visitedDt: any;
  buyerCid: FirebaseID | null;
  isFavorite: boolean;
  isExhibitor: boolean;
};

export type BrandOption = {
  value: string;
  text: string;
};

export type BoothOption = {
  value: string;
  text: string;
};

export type ItemOption = {
  value: string;
  text: string;
};

export type DataType = {
  loading: boolean;
  brands: Brand[];
  booths: Booth[];
  items: Item[];
  brandOptions: BrandOption[];
  boothOptions: BoothOption[];
  itemOptions: ItemOption[];
  selectedTarget: string;
  visitors: VisitorInfo[];
  favoriteOwners: Set<FirebaseID>;
  initialLimit: number;
  hasMore: boolean;
};

export default Vue.extend({
  name: 'exhibitor-dashboard-visitors',
  data(): DataType {
    return {
      loading: false,
      brands: [],
      booths: [],
      items: [],
      brandOptions: [],
      boothOptions: [],
      itemOptions: [],
      selectedTarget: 'all',
      visitors: [],
      favoriteOwners: new Set(),
      initialLimit: 100,
      hasMore: false,
    };
  },
  async mounted() {
    try {
      this.loading = true;
      this.$loading(true);
      const cid = this.$auth.cid;
      if (!cid) throw new Error('cid is null');

      // ブランド一覧を取得
      this.brands = await brand.loadsByOwner(cid);
      this.brandOptions = this.brands.map((b) => ({
        value: b.id || '',
        text: b.data.base.name,
      }));

      // 最初のブランドでブースと商品を取得
      if (this.brandOptions.length > 0) {
        const firstBrandId = this.brandOptions[0].value;
        await this.loadBoothsAndItems(firstBrandId);
        await this.loadVisitors('all', null);
      }
    } catch (error: any) {
      this.$error('データの取得に失敗しました。', error);
    } finally {
      this.loading = false;
      this.$loading(false);
    }
  },
  methods: {
    async onTargetChange() {
      // ブランドが選択された場合、そのブランドのブースと商品を取得
      if (this.selectedTarget.startsWith('brand-')) {
        const brandId = this.selectedTarget.replace('brand-', '');
        await this.loadBoothsAndItems(brandId);
      }
      // リセット
      this.visitors = [];
      this.hasMore = false;
      await this.loadVisitors(this.selectedTarget, null);
    },
    async loadMore() {
      // 現在の表示件数を増やして再取得
      this.initialLimit += 100;
      await this.loadVisitors(this.selectedTarget, null);
    },
    async loadBoothsAndItems(brandId: FirebaseID) {
      const cid = this.$auth.cid;
      if (!cid) return;

      // ブース一覧を取得
      this.booths = await booth.loadsByBrandId(cid, brandId);
      this.boothOptions = this.booths.map((b) => ({
        value: b.id || '',
        text: b.data.base.name,
      }));

      // 商品一覧を取得
      this.items = await item.loadsByBrandId(cid, brandId);
      this.itemOptions = this.items.map((i) => ({
        value: i.id || '',
        text: i.data.base.name,
      }));
    },
    async loadVisitors(target: string, brandIdOverride: FirebaseID | null) {
      try {
        this.loading = true;
        this.$loading(true);
        this.visitors = [];

        const cid = this.$auth.cid;
        if (!cid) throw new Error('cid is null');

        // 1. 閲覧データを取得
        const { allVisitorData, hasMore } = await this.loadVisitorData(target, brandIdOverride);
        if (hasMore !== undefined) {
          this.hasMore = hasMore;
        }

        // 2. 閲覧データからvisitorInfoを構築
        const visitorInfos = await this.buildVisitorInfos(allVisitorData);

        // 3. 閲覧日時でソート（新しい順）
        visitorInfos.sort((a, b) => {
          const aDt = a.visitedDt?.toDate?.() || new Date(0);
          const bDt = b.visitedDt?.toDate?.() || new Date(0);
          return bDt.getTime() - aDt.getTime();
        });

        // 「全て」の場合は、hasMoreを判定（簡易的に件数で判定）
        if (target === 'all') {
          this.hasMore = visitorInfos.length >= this.initialLimit;
        }

        this.visitors = visitorInfos;
      } catch (error: any) {
        this.$error('来場者の取得に失敗しました。', error);
      } finally {
        this.loading = false;
        this.$loading(false);
      }
    },
    /**
     * 閲覧データを取得（並列化）
     */
    async loadVisitorData(
      target: string,
      brandIdOverride: FirebaseID | null,
    ): Promise<{ allVisitorData: any[]; hasMore?: boolean }> {
      let allVisitorData: any[] = [];
      let hasMore: boolean | undefined;

      if (target === 'all') {
        // 全て: 最初のブランドの全てのデータを取得
        if (this.brandOptions.length === 0) return { allVisitorData: [] };
        const brandId = brandIdOverride || this.brandOptions[0].value;

        // ブランド、ブース、商品の閲覧データを並列取得
        const validBooths = this.booths.filter((bt): bt is Booth & { id: FirebaseID } => bt.id !== null && bt.id !== undefined);
        const validItems = this.items.filter((it): it is Item & { id: FirebaseID } => it.id !== null && it.id !== undefined);
        const promises = [
          analytics.calcPVOfBrandLatest(brandId, this.initialLimit),
          ...validBooths.map((bt) => analytics.calcPVOfBoothLatest(brandId, bt.id, this.initialLimit)),
          ...validItems.map((it) => analytics.calcPVOfItemLatest(brandId, it.id, this.initialLimit)),
        ];
        const results = await Promise.all(promises);
        const brandPvData = results[0];
        const boothPvDataList = results.slice(1, 1 + validBooths.length);
        const itemPvDataList = results.slice(1 + validBooths.length);

        allVisitorData = allVisitorData.concat(brandPvData.ls || []);
        boothPvDataList.forEach((boothPvData) => {
          allVisitorData = allVisitorData.concat(boothPvData.ls || []);
        });
        itemPvDataList.forEach((itemPvData) => {
          allVisitorData = allVisitorData.concat(itemPvData.ls || []);
        });
      } else if (target.startsWith('brand-')) {
        // ブランド選択（最新N件のみ）
        const brandId = target.replace('brand-', '');
        const brandPvData = await analytics.calcPVOfBrandLatest(brandId, this.initialLimit);
        allVisitorData = allVisitorData.concat(brandPvData.ls || []);
        hasMore = brandPvData.pv >= this.initialLimit;
      } else if (target.startsWith('booth-')) {
        // ブース選択（最新N件のみ）
        const boothId = target.replace('booth-', '');
        const selectedBooth = this.booths.find((b) => b.id === boothId);
        if (selectedBooth && this.brandOptions.length > 0) {
          const brandId = this.brandOptions[0].value;
          const boothPvData = await analytics.calcPVOfBoothLatest(brandId, boothId, this.initialLimit);
          allVisitorData = allVisitorData.concat(boothPvData.ls || []);
          hasMore = boothPvData.pv >= this.initialLimit;
        }
      } else if (target.startsWith('item-')) {
        // 商品選択（最新N件のみ）
        const itemId = target.replace('item-', '');
        if (this.brandOptions.length > 0) {
          const brandId = this.brandOptions[0].value;
          const itemPvData = await analytics.calcPVOfItemLatest(brandId, itemId, this.initialLimit);
          allVisitorData = allVisitorData.concat(itemPvData.ls || []);
          hasMore = itemPvData.pv >= this.initialLimit;
        }
      }

      return { allVisitorData, hasMore };
    },
    /**
     * 閲覧データからvisitorInfoを構築（並列化）
     */
    async buildVisitorInfos(allVisitorData: any[]): Promise<VisitorInfo[]> {
      // ユニークなuidのリストを取得
      const uniqueUids = Array.from(new Set(allVisitorData.map((v) => v.uid).filter((uid) => uid)));

      // buyerとbuyerCompanyを並列取得
      const buyerPromises = uniqueUids.map((uid) => buyerM.load(uid));
      const buyers = await Promise.all(buyerPromises);

      // buyerCompanyを並列取得
      const buyerCids = buyers
        .filter((b): b is Buyer => b !== null && b !== undefined && b.data.cid !== null && b.data.cid !== undefined)
        .map((b) => b.data.cid)
        .filter((cid): cid is FirebaseID => cid !== null && cid !== undefined)
        .filter((cid, index, self) => self.indexOf(cid) === index); // ユニーク化

      const buyerCompanyPromises = buyerCids.map((cid) => buyerCompanyM.load(cid));
      const buyerCompanies = await Promise.all(buyerCompanyPromises);

      // キャッシュ構築（buyer/buyerCompanyは同じuidが複数回出現する可能性があるため）
      const buyerCache = new Map<FirebaseID, Buyer>();
      buyers.forEach((buyer, index) => {
        if (buyer) {
          buyerCache.set(uniqueUids[index], buyer);
        }
      });

      const buyerCompanyCache = new Map<FirebaseID, BuyerCompany>();
      buyerCompanies.forEach((company, index) => {
        if (company) {
          buyerCompanyCache.set(buyerCids[index], company);
        }
      });

      // 各閲覧データに対してエントリを作成
      const visitorInfos: VisitorInfo[] = [];

      for (const visitData of allVisitorData) {
        const uid = visitData.uid;
        if (!uid) continue;

        try {
          const buyer = buyerCache.get(uid);
          if (!buyer || !buyer.data.cid) continue;

          const buyerCompany = buyerCompanyCache.get(buyer.data.cid);
          if (!buyerCompany) continue;

          // 展示会ユーザかどうかを判定
          const isExhibitor = (buyerCompany.data as any).exhibitor === true;

          // 閲覧先とお気に入り状態を取得
          const { visitedTarget, isFavorite } = await this.getVisitedTargetAndFavorite(visitData, uid);
          visitorInfos.push({
            uid,
            companyName: buyerCompany.data.name,
            visitedTarget,
            visitedDt: visitData.visitedDt || null,
            buyerCid: buyer.data.cid,
            isFavorite,
            isExhibitor,
          });
        } catch (error: any) {
          // エラーは無視して続行
        }
      }

      return visitorInfos;
    },
    /**
     * 閲覧先とお気に入り状態を取得
     */
    async getVisitedTargetAndFavorite(
      visitData: any,
      uid: FirebaseID,
    ): Promise<{ visitedTarget: string; isFavorite: boolean }> {
      let visitedTarget = '-';
      let isFavorite = false;
      const visitedBrandId: FirebaseID | null = visitData.brandId || null;

      if (visitData.itemId) {
        // 商品ページ
        const visitedItem = this.items.find((it) => it.id === visitData.itemId);
        if (visitedItem) {
          visitedTarget = `商品: ${visitedItem.data.base.name}`;
        } else {
          visitedTarget = `商品: ${visitData.itemId}`;
        }
        // 商品をお気に入りにしているか判定
        const itemOwnerIds = await favorite.loadFavoriteOwnerIdsByItem(visitData.itemId);
        if (itemOwnerIds.includes(uid)) {
          isFavorite = true;
        } else if (visitedBrandId) {
          // ブランドをお気に入りにしているか判定
          const brandOwnerIds = await favorite.loadFavoriteOwnerIdsByBrand(visitedBrandId);
          if (brandOwnerIds.includes(uid)) {
            isFavorite = true;
          }
        }
      } else if (visitData.boothId) {
        // ブースページ
        const visitedBooth = this.booths.find((bt) => bt.id === visitData.boothId);
        if (visitedBooth) {
          visitedTarget = `ブース: ${visitedBooth.data.base.name}`;
        } else {
          visitedTarget = `ブース: ${visitData.boothId}`;
        }
        // ブースにはお気に入り機能がないため、isFavoriteはfalseのまま
      } else if (visitData.brandId) {
        // ブランドページ
        const visitedBrand = this.brands.find((b) => b.id === visitData.brandId);
        if (visitedBrand) {
          visitedTarget = `ブランド: ${visitedBrand.data.base.name}`;
        } else {
          visitedTarget = `ブランド: ${visitData.brandId}`;
        }
        // ブランドをお気に入りにしているか判定
        if (visitedBrandId) {
          const brandOwnerIds = await favorite.loadFavoriteOwnerIdsByBrand(visitedBrandId);
          if (brandOwnerIds.includes(uid)) {
            isFavorite = true;
          }
        }
      }

      return { visitedTarget, isFavorite };
    },
    formatDateTime(dt: any): string {
      if (!dt) return '-';
      return calender.formatDateTime(dt);
    },
    async onContact(visitor: VisitorInfo) {
      try {
        this.$loading(true);
        // 選択されたターゲットからブランドIDを取得
        let brandId: string | null = null;
        if (this.selectedTarget === 'all' && this.brandOptions.length > 0) {
          brandId = this.brandOptions[0].value;
        } else if (this.selectedTarget.startsWith('brand-')) {
          brandId = this.selectedTarget.replace('brand-', '');
        } else if (this.selectedTarget.startsWith('booth-') && this.brandOptions.length > 0) {
          brandId = this.brandOptions[0].value;
        } else if (this.selectedTarget.startsWith('item-') && this.brandOptions.length > 0) {
          brandId = this.brandOptions[0].value;
        }

        if (!brandId) {
          throw new Error('ブランドIDが取得できません');
        }

        const uid = this.$auth.uid;
        const exhibitorCId = this.$auth.cid;
        const buyerCid = visitor.buyerCid;

        if (!exhibitorCId || !buyerCid || !brandId) {
          throw new Error('必要な情報が不足しています');
        }

        // 既存のコンタクトを確認
        const contactId = await contact.existsContact(brandId, buyerCid, exhibitorCId);

        if (contactId) {
          await this.$success('お問い合わせ済みです');
          this.$router.push({
            name: 'exhibitor-contact-chat',
            params: {
              id: contactId,
            },
          });
          return;
        }

        // 新しいコンタクトを作成
        const contactFrom: 'exhibitor' | 'buyer' = 'exhibitor';
        const newContact = await createNewContact(
          exhibitorCId,
          buyerCid,
          brandId,
          contactFrom,
        );

        const res = await contact.save(uid, newContact);
        if (!res.id) {
          throw new Error('Can not send contact');
        }
        await this.$success('お問い合わせを完了しました');
        this.$router.push({
          name: 'exhibitor-contact-chat',
          params: {
            id: res.id,
          },
        });
      } catch (error: any) {
        this.$error('お問い合わせに失敗しました。', error);
      } finally {
        this.$loading(false);
      }
    },
  },
});
